import React, { useEffect, useState } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import { Button } from 'components/index';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import { changeUserPassword } from './auth.api';
import { toast } from 'react-toastify';
// import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose }) => {
  // State for input fields and validation
  const [oldPassword, setOldPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordValid, setPasswordValid] = useState(true);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordValid(true);
      setShowOldPassword(false);
      setShowNewPassword(false);
      setShowConfirmPassword(false);
      setError('');
    }
  }, [open]);

  const togglePasswordVisibility = (field: any) => {
    if (field === 'old') setShowOldPassword(!showOldPassword);
    else if (field === 'new') setShowNewPassword(!showNewPassword);
    else if (field === 'confirm') setShowConfirmPassword(!showConfirmPassword);
  };

  const handleNewPasswordChange = (e: { target: { value: any } }) => {
    const newPassword = e.target.value;
    setNewPassword(newPassword);

    // Validate new password
    const isValid =
      newPassword.length >= 6 && /\d/.test(newPassword) && /[a-z]/.test(newPassword) && /[A-Z]/.test(newPassword) && /\W/.test(newPassword);
    setPasswordValid(isValid);
  };

  const handleSave = async () => {
    if (oldPassword === newPassword) {
      toast.error('New password must be different from the old password.');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New password and confirm password do not match.');
      // setError('New password and confirm password do not match.');
      return;
    }
    if (!passwordValid) {
      toast.error('Invalid new password.');
      setError('Invalid new password.');
      return;
    }
    setIsLoading(true);
    try {
      const response = await changeUserPassword(oldPassword, newPassword);
      toast.success(response.message);
      setIsLoading(false);
      setOldPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // Handle success
      setError('');
      onClose();
      // Optionally, show a success message
    } catch (error: any) {
      setIsLoading(false);
      console.log(error.message);
      toast.error(error.message || error.response?.data?.message || 'Failed to change password. Please try again.');
      setError(error.message || 'Failed to change password. Please try again.');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} aria-labelledby='change-password-dialog-title' fullWidth maxWidth='sm'>
      <DialogTitle id='change-password-dialog-title'>Change Password</DialogTitle>
      <DialogContent>
        {/* {isLoading && <LinearProgress sx={{ width: '100%' }} />} */}
        {/* {error && <Alert severity='error'>{error}</Alert>} */}
        <TextField
          required
          margin='dense'
          label='Old Password'
          fullWidth
          variant='outlined'
          value={oldPassword}
          onChange={e => setOldPassword(e.target.value)}
          type={showOldPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton size='small' onClick={() => togglePasswordVisibility('old')}>
                  {showOldPassword ? <VisibilityOffOutlined /> : <VisibilityOutlinedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          required
          margin='dense'
          label='New Password'
          fullWidth
          variant='outlined'
          value={newPassword}
          onChange={handleNewPasswordChange}
          error={!passwordValid}
          helperText={
            !passwordValid
              ? 'Password must be at least 6 characters long, include a digit, a lowercase letter, an uppercase letter, and a special character.'
              : ''
          }
          type={showNewPassword ? 'text' : 'password'}
          // ... other props
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton size='small' onClick={() => togglePasswordVisibility('new')}>
                  {showNewPassword ? <VisibilityOffOutlined /> : <VisibilityOutlinedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          required
          margin='dense'
          label='Confirm New Password'
          fullWidth
          variant='outlined'
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          type={showConfirmPassword ? 'text' : 'password'}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton size='small' onClick={() => togglePasswordVisibility('confirm')}>
                  {showConfirmPassword ? <VisibilityOffOutlined /> : <VisibilityOutlinedIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button variant='outlined' color='error' onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSave} variant='contained' color='primary' disabled={!passwordValid || isLoading}>
          {isLoading && <CircularProgress size={24} color='inherit' style={{ marginRight: '10px' }} />}
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
